import { useEffect } from 'react'
import { isKeyboardUser } from '@/features/shared/utils/detectKeyboardUser'

// Custom hook that checks if a user clicks outside the ref or a keyboard user navigates outside the ref.
// We use shouldCheck to prevent any unnecessary func calls.
export const useDetectFocusOutside = (
  refs: React.RefObject<HTMLElement | null> | React.RefObject<HTMLElement | null>[],
  func: () => void,
  shouldCheck = true,
) => {
  useEffect(() => {
    // Allow for multiple refs
    const refArray = Array.isArray(refs) ? refs : [refs]

    const checkIsOutside = (e: Event) => {
      return refArray.every(
        (ref) => ref.current && e.target instanceof Node && !ref.current.contains(e.target),
      )
    }

    const detectClickOutside = (e: MouseEvent | TouchEvent) => {
      const isOutside = checkIsOutside(e)
      if (shouldCheck && isOutside) {
        func()
      }
    }

    const detectKeyboardFocusOutside = (e: FocusEvent) => {
      const isOutside = checkIsOutside(e)
      if (shouldCheck && isKeyboardUser() && isOutside) {
        func()
      }
    }

    window.addEventListener('click', detectClickOutside, true)
    window.addEventListener('touchstart', detectClickOutside, true)
    window.addEventListener('focusin', detectKeyboardFocusOutside, true)
    return () => {
      window.removeEventListener('click', detectClickOutside, true)
      window.removeEventListener('touchstart', detectClickOutside, true)
      window.removeEventListener('focusin', detectKeyboardFocusOutside, true)
    }
  }, [func, refs, shouldCheck])
}
